import * as React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { DraggableProvidedDraggableProps, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        slot: {
            position: 'relative',
            '&:hover $handle': {
                opacity: 1,
            },
        },
        // react-beautiful-dnd already lifts the dragged item above everything else and gives
        // it its own drag shadow/scale via its own inline style (see the render site) - this
        // only adds a touch more definition on top of that, matched to every widget's own
        // standard 8px radius (explicit bug report on an earlier hand-rolled version: without
        // a matching radius here, this shadow rendered as a square box around the card's own
        // rounded corners instead of following them).
        dragging: {
            borderRadius: 8,
            boxShadow: '0 12px 28px rgba(16,26,51,0.22)',
        },
        // Top-RIGHT corner, inside the widget. Sized/positioned to sit entirely within the
        // smallest padding every widget uses (16px, KpiSummaryWidget.tsx/DueListWidget.tsx/
        // QuickActionsWidget.tsx's own `root` - YourEnquiriesWidget.tsx/YourTasksWidget.tsx use
        // 20px, more room still) - explicit bug report that a bigger handle here collided with
        // KpiSummaryWidget.tsx's own "open full dashboard" arrow link, which starts right at
        // that same padding boundary. A 14px handle inset by 2px never crosses into it in any
        // of the six widgets. Hidden until the card is hovered so six handles don't clutter
        // the panel at rest.
        handle: {
            position: 'absolute',
            top: 2,
            right: 2,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 14,
            height: 14,
            borderRadius: 4,
            color: theme.palette.text.secondary,
            cursor: 'grab',
            touchAction: 'none',
            backgroundColor: 'rgba(255,255,255,0.9)',
            opacity: 0,
            transition: 'opacity 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                backgroundColor: '#FFFFFF',
            },
        },
        // Kept visible/active for the whole gesture even if the pointer drifts off the handle
        // itself mid-drag (react-beautiful-dnd keeps tracking the gesture regardless, but CSS
        // :hover wouldn't).
        handleActive: {
            opacity: 1,
            cursor: 'grabbing',
        },
    })
);

export interface QuickViewWidgetSlotProps {
    isDragging: boolean;
    draggableProps: DraggableProvidedDraggableProps;
    dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
    /** Which widget this is, so the Quick View tour can point at it (data-tour) */
    tourKey?: string;
    children: React.ReactNode;
}

// Wraps one Quick View widget with a drag handle - shared across all six widgets
// (QuickViewDrawer.tsx's own render) rather than each widget file managing its own drag
// affordance, since the handle/lifted-state treatment is identical everywhere and the widget
// itself has no idea where it currently sits in the list. draggableProps/dragHandleProps come
// straight from react-beautiful-dnd's own Draggable render prop - dragHandleProps is scoped to
// just the small handle span below (not the whole card), so the rest of each widget's own
// content (buttons, links, text selection) keeps working normally.
export const QuickViewWidgetSlot = React.forwardRef<HTMLDivElement, QuickViewWidgetSlotProps>((props, ref) => {
    const classes = useStyles();

    return (
        <div
            ref={ref}
            {...props.draggableProps}
            className={`${classes.slot} ${props.isDragging ? classes.dragging : ''}`}
            data-tour={props.tourKey ? `quick-view-widget-${props.tourKey}` : undefined}
        >
            <span
                {...props.dragHandleProps}
                className={`${classes.handle} ${props.isDragging ? classes.handleActive : ''}`}
                aria-label="Drag to reorder this widget"
            >
                <DragIndicatorIcon style={{ fontSize: 11 }} />
            </span>
            {props.children}
        </div>
    );
});
QuickViewWidgetSlot.displayName = 'QuickViewWidgetSlot';
